/** Shared keyword analytics + gradient helpers for the landing page (/) and related routes. */

import {
  POLE_COLORS,
  filterCategoryCountsForArtefact,
  findArtefactCategoryBucket,
  normalizeCategoryCountMap,
} from '../config/ethicalDimensions';
import { enhanceColorVisibility } from './blobColorUtils';

export const LANDING_MAX_ARTEFACT_CELLS = 7;
/** Keyword analytics poll on / intro only; personas load once per visit (or language change). */
export const LANDING_REFRESH_MS = 10 * 60 * 1000;
export const LANDING_PRIMARY_KEYWORD_WINDOW_HOURS = 24;
/** Wide window for keyword marquee / blob colors; combined with LANDING_ANALYTICS_SINCE on the API. */
export const LANDING_BLOB_COLOR_WINDOW_HOURS = 99999;
/** Wide window for keyword marquee when the 24h snapshot is sparse. */
export const LANDING_FALLBACK_KEYWORD_WINDOW_HOURS = LANDING_BLOB_COLOR_WINDOW_HOURS;
/** Landing keyword marquee + blob ethical-class aggregates include only rows on/after this date (Europe/Zurich). */
export const LANDING_ANALYTICS_SINCE = '2026-06-21';
/** Below this many distinct phrases in the primary window, fetch all-time aggregates instead. */
export const LANDING_MIN_DISTINCT_KEYWORDS = 20;
export const LANDING_KEYWORD_STEP_MS = 1000;

/** Fallback loop duration (seconds) before the marquee track width is measured. */
export const LANDING_MARQUEE_FALLBACK_DURATION_SEC = 440;

/** Constant horizontal scroll speed for the landing keyword marquee (px/s). */
export const LANDING_MARQUEE_PIXELS_PER_SEC = 4.5;

/** One loop duration from measured track width (CSS animation moves −50% of track). */
export function landingMarqueeDurationFromTrackWidth(trackWidthPx) {
  const loopDistancePx = trackWidthPx / 2;
  if (loopDistancePx <= 0) return LANDING_MARQUEE_FALLBACK_DURATION_SEC;
  return loopDistancePx / LANDING_MARQUEE_PIXELS_PER_SEC;
}

/** @deprecated Use POLE_COLORS from ethicalDimensions; kept for existing imports. */
export const CATEGORY_COLORS = POLE_COLORS;

export const FALLBACK_COLOR = '#d3d3d3';

function emptyAnalyticsDataStub(data, language) {
  return {
    categories: {},
    artefact_categories: {},
    total_keywords: 0,
    total_sessions: 0,
    last_updated: data?.last_updated || new Date().toISOString(),
    language,
  };
}

/**
 * When the UI requests a locale (e.g. el), only trust aggregates that the API
 * explicitly tagged with the same `data.language`. Otherwise we may show an
 * unfiltered English-heavy mix if the query param was dropped or an old server
 * ignored it.
 */
export function ensureKeywordAnalyticsMatchesUiLanguage(analyticsResult, requestedLang) {
  if (requestedLang == null || String(requestedLang).trim() === '') {
    return analyticsResult;
  }
  if (!analyticsResult || !analyticsResult.success) {
    return analyticsResult;
  }
  const want = String(requestedLang).trim().toLowerCase();
  const data = analyticsResult.data;
  if (!data || typeof data !== 'object') {
    return analyticsResult;
  }
  if (data.language_filter_available === false) {
    return analyticsResult;
  }
  const got = data.language;
  if (got == null || String(got).trim() === '') {
    if (want === 'en') {
      return analyticsResult;
    }
    return {
      success: true,
      data: emptyAnalyticsDataStub(data, want),
    };
  }
  if (String(got).trim().toLowerCase() !== want) {
    return {
      success: true,
      data: emptyAnalyticsDataStub(data, want),
    };
  }
  return analyticsResult;
}

export function normalizeName(value) {
  if (typeof value !== 'string') return '';
  return value.toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function hashString(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function getTopKeywordList(analyticsData) {
  if (!analyticsData?.categories) return [];
  const keywordCounts = new Map();

  Object.values(analyticsData.categories).forEach((categoryData) => {
    if (!Array.isArray(categoryData?.keywords)) return;
    categoryData.keywords.forEach((item) => {
      if (!item?.keyword) return;
      const current = keywordCounts.get(item.keyword) || 0;
      keywordCounts.set(item.keyword, current + (item.count || 1));
    });
  });

  return Array.from(keywordCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([keyword, count]) => ({ keyword, count }));
}

export function countDistinctKeywords(analyticsData) {
  return getTopKeywordList(analyticsData).length;
}

export function getTemporalKeywordList(analyticsData) {
  if (!analyticsData) return [];

  if (Array.isArray(analyticsData.keyword_timeline) && analyticsData.keyword_timeline.length > 0) {
    return analyticsData.keyword_timeline
      .filter((entry) => typeof entry?.keyword === 'string' && entry.keyword.trim().length > 0)
      .sort((a, b) => {
        const aTime = new Date(a.created_at || 0).getTime();
        const bTime = new Date(b.created_at || 0).getTime();
        return aTime - bTime;
      })
      .map((entry) => ({ keyword: entry.keyword.trim() }));
  }

  const flattened = [];
  const categories = analyticsData.categories || {};
  Object.values(categories).forEach((categoryData) => {
    if (!Array.isArray(categoryData?.keywords)) return;
    categoryData.keywords.forEach((item) => {
      if (typeof item?.keyword === 'string' && item.keyword.trim().length > 0) {
        flattened.push({ keyword: item.keyword.trim() });
      }
    });
  });
  return flattened;
}

/**
 * Category counts for one artefact's landing blob.
 * Expects wide-window analytics with LANDING_ANALYTICS_SINCE applied server-side.
 */
export function getArtefactCategoryCounts(artefactName, analyticsData, personas = null) {
  const raw = findArtefactCategoryBucket(analyticsData?.artefact_categories, artefactName);
  if (!raw) {
    return {};
  }
  return filterCategoryCountsForArtefact(
    normalizeCategoryCountMap(raw),
    artefactName,
    personas
  );
}

/** Ordered persona keys for landing grid cells (persona_order first, then remainder). */
export function orderedLandingArtefactNames(personas, personaOrder, max = LANDING_MAX_ARTEFACT_CELLS) {
  const keys = personas && typeof personas === 'object' ? Object.keys(personas) : [];
  const order = Array.isArray(personaOrder) ? personaOrder : [];
  const orderedNames = order.length
    ? [...order.filter((n) => keys.includes(n)), ...keys.filter((n) => !order.includes(n))]
    : keys;
  return orderedNames.slice(0, max);
}

export function buildArtefactColorMap(artefactNames, analyticsData, personas = null) {
  const nextColors = {};
  if (!Array.isArray(artefactNames)) return nextColors;
  artefactNames.forEach((artefactName) => {
    const artefactCategoryCounts = getArtefactCategoryCounts(artefactName, analyticsData, personas);
    nextColors[artefactName] = getGradientFromCounts(artefactCategoryCounts, artefactName);
  });
  return nextColors;
}

/** Keyword marquee data: 24h window, widened only when that snapshot is sparse. */
export async function fetchKeywordAnalyticsForMarquee(apiService, language) {
  let keywordAnalyticsResult = ensureKeywordAnalyticsMatchesUiLanguage(
    await apiService.getKeywordAnalytics(
      LANDING_PRIMARY_KEYWORD_WINDOW_HOURS,
      language,
      LANDING_ANALYTICS_SINCE,
    ),
    language
  );
  const primaryData = keywordAnalyticsResult?.data;
  if (shouldUseWideKeywordAnalyticsWindow(primaryData)) {
    const fallbackResult = ensureKeywordAnalyticsMatchesUiLanguage(
      await apiService.getKeywordAnalytics(
        LANDING_FALLBACK_KEYWORD_WINDOW_HOURS,
        language,
        LANDING_ANALYTICS_SINCE,
      ),
      language
    );
    if (fallbackResult?.success) {
      keywordAnalyticsResult = fallbackResult;
    }
  }
  return keywordAnalyticsResult?.success ? keywordAnalyticsResult.data : null;
}

/** Landing blob colors: wide window + LANDING_ANALYTICS_SINCE per-artefact ethical class counts. */
export async function fetchLandingBlobColorAnalytics(apiService, language) {
  const raw = await apiService.getKeywordAnalytics(
    LANDING_BLOB_COLOR_WINDOW_HOURS,
    language,
    LANDING_ANALYTICS_SINCE,
  );
  const result = ensureKeywordAnalyticsMatchesUiLanguage(raw, language);
  return result?.success ? result.data : null;
}

function hasClassCountsForLandingColors(analyticsData) {
  if (!analyticsData || typeof analyticsData !== 'object') return false;
  const byArtefact = analyticsData.artefact_categories;
  return Boolean(byArtefact && typeof byArtefact === 'object' && Object.keys(byArtefact).length > 0);
}

/** True when analytics payload has no per-artefact pole counts (old API or empty window). */
export function needsWideKeywordAnalyticsWindow(analyticsData) {
  if (!analyticsData || typeof analyticsData !== 'object') return true;
  const byArtefact = analyticsData.artefact_categories;
  if (!byArtefact || typeof byArtefact !== 'object') return true;
  return Object.keys(byArtefact).length === 0;
}

/** True when the primary (24h) window should be replaced with the all-time fallback fetch. */
export function shouldUseWideKeywordAnalyticsWindow(analyticsData) {
  if ((analyticsData?.total_keywords || 0) === 0 && !hasClassCountsForLandingColors(analyticsData)) {
    return true;
  }
  if (!hasClassCountsForLandingColors(analyticsData)) return true;
  return countDistinctKeywords(analyticsData) < LANDING_MIN_DISTINCT_KEYWORDS;
}

export function getGradientFromCounts(categoryCounts, artefactName) {
  const entries = Object.entries(categoryCounts || {})
    .filter(([, count]) => Number(count) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));

  if (entries.length === 0) {
    return { c1: FALLBACK_COLOR, c2: FALLBACK_COLOR, c3: FALLBACK_COLOR };
  }

  const offset = hashString(artefactName || '') % Math.max(entries.length, 1);
  const pick = (index) => {
    const category = entries[(offset + index) % entries.length]?.[0];
    const raw = POLE_COLORS[category] || FALLBACK_COLOR;
    return raw === FALLBACK_COLOR ? raw : enhanceColorVisibility(raw);
  };

  return { c1: pick(0), c2: pick(1), c3: pick(2) };
}
